/**
 * Every screen a call has, per design/design_handoff_calls: outgoing, incoming,
 * active voice, active video, the degraded states, and the ended card.
 *
 * One component rather than several because they are one surface that changes
 * shape — the identity block, the controls and the video layer are shared, and
 * splitting them apart would mean threading the same dozen values through four
 * files.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Avatar } from '../components/Avatar.tsx';
import { connectingStatus } from '../lib/callConnecting.ts';
import { isPipTap, pipDrag, pipSpot, type Point } from '../lib/callPip.ts';
import { formatDuration } from '../lib/format.ts';
import { describeTrouble, type PreviewTrouble } from '../lib/livePreview.ts';
import { setCallLayout } from '../lib/nativeCallPeer.ts';
import { useLivePicture } from '../lib/useLivePicture.ts';
import { useCall, type CallState } from './CallContext.tsx';
import {
  ChevronDownIcon,
  FlipIcon,
  MicIcon,
  PhoneIcon,
  SpeakerIcon,
  VideoIcon,
} from './CallIcons.tsx';
import './CallOverlay.css';

/** How long the controls stay up after the last tap, per the handoff. */
const CONTROLS_HIDE_MS = 3000;

/**
 * When the outgoing status line stops saying «Соединение…» and starts saying
 * «Вызов…» — the moment ringback would begin on a real telephone.
 */
const RINGBACK_AT_MS = 1500;

/**
 * And when it admits the other phone may be asleep.
 *
 * The handoff has no such line: it assumed an unreachable peer would have the
 * call button disabled. This app deliberately leaves the button live, because
 * «не в сети» here means only "no open socket" and says nothing about whether a
 * push can still wake them — which is the ordinary way one person reaches
 * another. The wait that buys has to be explained rather than left to look
 * broken.
 */
const NOTIFIED_AT_MS = 6000;

/** The status line under the name, which is where most of the state is legible. */
function statusFor(call: CallState, elapsedMs: number): string {
  if (call.phase === 'busy') return 'Занято';
  if (call.phase === 'lost') return call.message ?? 'Не удалось соединиться';
  if (call.phase === 'permission') return call.message ?? 'Нет доступа к камере и микрофону';
  if (call.phase === 'ended') return call.endReason === 'declined' ? 'Отклонён' : 'Звонок завершён';
  if (call.phase === 'incoming') return call.video ? 'Видеозвонок…' : 'Входящий звонок…';
  if (call.phase === 'outgoing') {
    if (elapsedMs < RINGBACK_AT_MS) return 'Соединение…';
    return elapsedMs < NOTIFIED_AT_MS ? 'Вызов…' : 'Вызов… Уведомление отправлено';
  }
  // Answered and still no media path — the identity block is on screen for a
  // voice call, so the wait is told under the name; see connectingStatus.
  if (call.phase === 'active' && call.connecting) return connectingStatus(elapsedMs);
  return '';
}


/**
 * One of the call's pictures: the stream bound to whichever element is on
 * screen, and kept painting for as long as it is.
 *
 * Both halves matter and both are about the element rather than the stream.
 * The binding is documented in lib/useLivePicture.ts — a minimised call
 * unmounts these elements while the stream stays the same object, so anything
 * keyed on the stream alone never re-binds and the call comes back black. The
 * watchdog is documented in lib/livePreview.ts — a WebView that was put away
 * returns holding live streams and painting nothing, first on one picture and
 * then on both.
 *
 * Nothing is filed for the ordinary case. A row here means a picture went
 * missing on somebody's screen while the call itself was fine — the one failure
 * that leaves no trace in any statistic, because every byte arrived.
 */
function useCallPicture(
  stream: MediaStream | null,
  where: string,
): (node: HTMLVideoElement | null) => void {
  const { noteCall } = useCall();
  const onTrouble = useCallback(
    (trouble: PreviewTrouble) => {
      noteCall({
        detail: `call/video/repaired/${where}`,
        message: `${where} picture: ${describeTrouble(trouble)}`,
      });
    },
    [noteCall, where],
  );
  return useLivePicture(stream, onTrouble);
}

export function CallOverlay(): React.ReactElement | null {
  const {
    call,
    localStream,
    remoteStream,
    minimized,
    nativeCall,
    nativeCallVideo,
    systemRinging,
    answer,
    decline,
    hangUp,
    toggleMute,
    toggleCamera,
    toggleSpeaker,
    flip,
    setMinimized,
    retry,
    dismiss,
  } = useCall();

  /**
   * Which picture is the big one. Tapping the small square trades them, the
   * way every other calling app does it — the far end is what you want filling
   * the screen almost always, and «almost» is the whole reason this exists:
   * showing somebody what is behind you, or checking your own framing, is a
   * thing done with the back camera and a face-sized preview is not enough of
   * it.
   */
  const [swapped, setSwapped] = useState(false);
  const swapPictures = useCallback(() => setSwapped((was) => !was), []);

  // Callback refs rather than useRef: these two elements are unmounted and
  // rebuilt under an unchanged stream every time the call is minimised, and the
  // node is the only thing that changed. See useCallPicture.
  const bigVideo = useCallPicture(swapped ? localStream : remoteStream, swapped ? 'self-big' : 'remote');
  const localVideo = useCallPicture(localStream, 'outgoing');
  const [controlsVisible, setControlsVisible] = useState(true);
  const [ringingMs, setRingingMs] = useState(0);

  /**
   * Drives the «Соединение…» → «Вызов…» → «Уведомление отправлено» progression.
   *
   * Depends on the phase and the id rather than on the call, deliberately: the
   * call object changes identity once a second while the timer runs, and
   * restarting this interval on every one of those would reset the progression
   * to «Соединение…» forever.
   */
  const phase = call?.phase;
  const callId = call?.id;
  const connecting = call?.connecting === true;

  // The next call starts the ordinary way round, whatever the last one ended as.
  useEffect(() => setSwapped(false), [callId]);

  useEffect(() => {
    // The same clock carries the connecting wait: it restarts at zero when the
    // phase turns active, which is the moment that wait begins.
    const waiting =
      phase === 'outgoing' || phase === 'incoming' || (phase === 'active' && connecting);
    if (!waiting) {
      setRingingMs(0);
      return;
    }
    const startedAt = Date.now();
    const id = window.setInterval(() => setRingingMs(Date.now() - startedAt), 500);
    return () => window.clearInterval(id);
  }, [phase, callId, connecting]);

  const showControls = useCallback(() => {
    setControlsVisible(true);
  }, []);

  /**
   * Auto-hide. Only on an active video call: on a voice call there is nothing
   * underneath worth uncovering, and the handoff shows the controls permanently.
   */
  useEffect(() => {
    if (!call || call.phase !== 'active' || !call.video) {
      setControlsVisible(true);
      return;
    }
    if (!controlsVisible) return;
    const id = window.setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
    return () => window.clearTimeout(id);
  }, [call, controlsVisible]);

  /**
   * Android's back gesture collapses the call instead of leaving the app.
   *
   * Same trick the image viewer in ChatScreen uses: push a history entry while
   * the overlay is up, and treat popping it as "close this layer". Without it,
   * back during a call walks out of the conversation entirely.
   */
  // Keyed on the id and nothing else. Depending on the call object would push a
  // new history entry every second, because the elapsed timer replaces it.
  const overlayUp = callId !== undefined && !minimized;

  useEffect(() => {
    if (!overlayUp) return;
    window.history.pushState({ call: true }, '');
    const onPop = () => setMinimized(true);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [overlayUp, callId, setMinimized]);

  /**
   * The native picture. On the native path the video is an RTCMTLVideoView
   * *under* the WebView, so the page's own job is to get out of the way:
   * `native-call-video` on <html> turns html, body, #root and the overlay
   * transparent, and everything that must stay visible — controls, scrims,
   * the identity card — keeps its own background and simply covers what it
   * covers. Voice calls and every non-video phase never enter here: no views,
   * no transparency, the overlay exactly as on the web.
   *
   * Gated on `nativeCallVideo` — the shell's per-call confession that it
   * really draws — never on the path flag alone: over a shell that renders
   * nothing (веха B) the transparency showed the chat list through a live
   * call. Until the shell says yes, a native video call keeps the opaque
   * overlay and simply has no picture.
   */
  const nativeVideoUp =
    nativeCallVideo &&
    call?.video === true &&
    !minimized &&
    (phase === 'active' || phase === 'outgoing');

  useEffect(() => {
    if (!nativeVideoUp) return;
    // Views first, the class a frame later, so transparency never opens onto
    // a layer that is not there yet — the black-flash order from the plan. On
    // the way out the reverse: paint returns first, then the layout effect's
    // cleanup below detaches the views behind an already-opaque page.
    const frame = window.requestAnimationFrame(() =>
      document.documentElement.classList.add('native-call-video'),
    );
    return () => {
      window.cancelAnimationFrame(frame);
      document.documentElement.classList.remove('native-call-video');
      setCallLayout({ remote: false, local: 'none' });
    };
  }, [nativeVideoUp]);

  const facing = call?.facing;

  useEffect(() => {
    if (!nativeVideoUp) return;
    if (phase === 'active') {
      // Only the remote view is the overlay's to declare: the small square,
      // its rectangle and which picture is in it belong to PipView, which
      // reports them itself — an omitted field means «unchanged», which is
      // what keeps these two writers off each other's half.
      setCallLayout({ remote: true });
      return;
    }
    // The outgoing video screen is the local camera at full bleed, with the
    // peer's card over it — same shape as the web path's <video>.
    setCallLayout({ remote: false, local: 'full', mirror: facing === 'user' });
  }, [nativeVideoUp, phase, facing]);

  // The system is ringing and drawing its own screen for this call; a second
  // one underneath it is the mess this guard exists to prevent. See
  // CallState.ringRefused for the case where the system declined to ring and
  // the page takes both the sound and the screen back.
  if (!call || minimized || systemRinging) return null;

  const video = call.video && (call.phase === 'active' || call.phase === 'outgoing');
  const chrome = controlsVisible ? 'call-chrome' : 'call-chrome is-hidden';
  const ringing = call.phase === 'outgoing' || call.phase === 'incoming';

  return (
    <div
      className={video ? 'call-overlay is-video' : 'call-overlay'}
      onClick={video && call.phase === 'active' ? showControls : undefined}
    >
      {video && call.phase === 'active' && (
        <>
          {/* Muted, and it has to be: the sound comes out of the one element
              that survives minimising and a voice call, which is RemoteAudio in
              CallProvider. Unmuting this would play the far end twice. On the
              native path there is no element at all — the picture is a Metal
              view under a transparent page, and the scrims below cover it the
              same way they cover the video. */}
          {!nativeCall && (
            <video
              ref={bigVideo}
              className="call-video-remote"
              data-mirror={swapped && call.facing === 'user' ? '' : undefined}
              autoPlay
              playsInline
              muted
            />
          )}
          {/* Whoever is big, with their camera off. The far end gets its
              card; your own gets the icon the small square shows instead,
              because an avatar of yourself explains nothing you did not just
              do to yourself. */}
          {(swapped ? call.cameraOff : call.peerCameraOff) && (
            <div className="call-peer-off">
              {swapped ? (
                <VideoIcon off size={34} />
              ) : (
                <Avatar className="call-avatar" photoId={call.peerAvatarId} name={call.peerName} />
              )}
              <div className="call-status">
                {swapped ? 'Камера выключена' : 'Камера собеседника выключена'}
              </div>
            </div>
          )}
          <PipView
            stream={swapped ? remoteStream : localStream}
            off={swapped ? call.peerCameraOff : call.cameraOff}
            native={nativeCallVideo}
            mirror={!swapped && call.facing === 'user'}
            swapped={swapped}
            onSwap={swapPictures}
          />
        </>
      )}

      {/* The outgoing video screen is the local camera at full bleed, with the
          peer's card over it — the framing is visible while it rings. Native:
          the same picture, declared through setCallLayout above instead. */}
      {video && call.phase === 'outgoing' && !nativeCall && (
        <video
          ref={localVideo}
          className="call-video-remote"
          data-mirror={call.facing === 'user' ? '' : undefined}
          autoPlay
          playsInline
          muted
        />
      )}

      {/* The video screen has no identity block to carry the status, so the
          wait is a pill in the corner — the reconnect banner's shape without
          its alarm colour, because nothing has gone wrong yet. */}
      {video && call.phase === 'active' && call.connecting && (
        <div className="call-reconnect is-connecting">
          <span className="call-spinner" />
          {connectingStatus(ringingMs)}
        </div>
      )}

      {call.reconnecting && call.phase === 'active' && !call.connecting && (
        <div className="call-reconnect">
          <span className="call-spinner" />
          Соединение…
        </div>
      )}

      {call.phase === 'active' && call.video && (
        <button
          type="button"
          className={`call-minimize ${chrome}`}
          onClick={() => setMinimized(true)}
          aria-label="Свернуть звонок"
        >
          <ChevronDownIcon size={20} />
        </button>
      )}

      {/* The ended card drops the identity block entirely, per the handoff. */}
      {call.phase === 'ended' ? (
        <div className="call-identity">
          <div className="call-status">{statusFor(call, ringingMs)}</div>
        </div>
      ) : (
        (!video || call.phase !== 'active') && (
          <div className="call-identity">
            <Avatar
              className={ringing ? 'call-avatar is-ringing' : 'call-avatar'}
              photoId={call.peerAvatarId}
              name={call.peerName}
            />
            <div className="call-name">{call.peerName}</div>
            <div
              className={
                call.phase === 'busy' || call.phase === 'lost' || call.phase === 'permission'
                  ? 'call-status is-error'
                  : 'call-status'
              }
            >
              {statusFor(call, ringingMs)}
            </div>
            {call.phase === 'active' && (
              <div className="call-timer">{formatDuration(call.elapsedSeconds * 1000)}</div>
            )}
          </div>
        )
      )}

      {video && call.phase === 'active' && (
        <div className={`call-timer call-timer-float ${chrome}`}>
          {formatDuration(call.elapsedSeconds * 1000)}
        </div>
      )}

      {/* ---- the row at the bottom, which is different in every phase ---- */}

      {call.phase === 'incoming' && (
        <div>
          {call.video && (
            <button type="button" className="call-audio-only" onClick={() => answer(false)}>
              Ответить без видео
            </button>
          )}
          <div className="call-answer-row">
            <div className="call-answer-item">
              <button
                type="button"
                className="call-btn is-end"
                onClick={decline}
                aria-label="Отклонить"
              >
                <PhoneIcon size={26} />
              </button>
              Отклонить
            </div>
            <div className="call-answer-item">
              <button
                type="button"
                className="call-btn is-answer"
                onClick={() => answer(call.video)}
                aria-label="Ответить"
              >
                <PhoneIcon size={26} />
              </button>
              Ответить
            </div>
          </div>
        </div>
      )}

      {call.phase === 'outgoing' && (
        <div className="call-controls">
          <button type="button" className="call-btn is-end" onClick={hangUp} aria-label="Отменить">
            <PhoneIcon size={26} />
          </button>
        </div>
      )}

      {/* The row fades on a video call, per the handoff — but never the whole
          row: the button that ends the call stays. Auto-hide was written when
          the system call UI was underneath ours and offered its own red
          button; with CallKit gone this app is the only way to hang up, and a
          hang-up hidden behind «tap somewhere first» is how a call ends by
          force-quitting the app. Reported from a device, twice. */}
      {call.phase === 'active' && (
        <div className="call-controls">
          <span className={call.video ? `call-controls-fade ${chrome}` : 'call-controls-fade'}>
            <button
              type="button"
              className={call.muted ? 'call-btn is-active' : 'call-btn'}
              onClick={toggleMute}
              aria-label={call.muted ? 'Включить микрофон' : 'Выключить микрофон'}
            >
              <MicIcon off={call.muted} />
            </button>

            {call.video ? (
              <>
                <button
                  type="button"
                  className={call.cameraOff ? 'call-btn is-active' : 'call-btn'}
                  onClick={toggleCamera}
                  aria-label={call.cameraOff ? 'Включить камеру' : 'Выключить камеру'}
                >
                  <VideoIcon off={call.cameraOff} />
                </button>
                <button type="button" className="call-btn" onClick={flip} aria-label="Перевернуть камеру">
                  <FlipIcon />
                </button>
              </>
            ) : (
              <button
                type="button"
                className={call.speakerOn ? 'call-btn is-active' : 'call-btn'}
                onClick={toggleSpeaker}
                aria-label="Динамик"
              >
                <SpeakerIcon off={!call.speakerOn} />
              </button>
            )}
          </span>

          <button type="button" className="call-btn is-end" onClick={hangUp} aria-label="Завершить">
            <PhoneIcon size={26} />
          </button>
        </div>
      )}

      {(call.phase === 'busy' || call.phase === 'lost' || call.phase === 'permission') && (
        <div className="call-actions">
          <button type="button" className="call-action" onClick={dismiss}>
            {call.phase === 'permission' ? 'Отмена' : 'Закрыть'}
          </button>
          {call.phase === 'lost' && (
            <button type="button" className="call-action is-primary" onClick={retry}>
              Повторить
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The small picture: your own camera normally, the far end's once they have
 * been swapped.
 *
 * It goes wherever it is dropped and stays there — see lib/callPip.ts for the
 * arithmetic and for why the place is remembered as a fraction of the screen
 * rather than in pixels. A press that does not move is a tap, and a tap trades
 * the two pictures over.
 *
 * On the native path the square is the same square with nothing in it: both
 * pictures are the shell's, drawn under the page, and this component's job
 * becomes saying where the hole is and which of the two belongs in it — see
 * the rect reporting below.
 */
export function PipView({
  stream,
  off,
  native,
  mirror,
  swapped,
  onSwap,
}: {
  stream: MediaStream | null;
  off: boolean;
  native: boolean;
  /** Front camera only, and never the far end: framing works like a mirror. */
  mirror: boolean;
  /** The far end is in here and the self-view fills the screen. */
  swapped: boolean;
  onSwap: () => void;
}): React.ReactElement {
  // Same callback ref as the overlay's two, and for one more reason of its own:
  // this element also goes away whenever the camera is switched off, and came
  // back sourceless when it was switched on again.
  const video = useCallPicture(native ? null : stream, swapped ? 'peer-pip' : 'self');
  const box = useRef<HTMLDivElement | null>(null);
  /** Where it rests, in fractions of its travel. Null until it is first moved. */
  const [spot, setSpot] = useState<Point | null>(null);
  const [drag, setDrag] = useState<{ grab: Point; from: Point; at: Point | null } | null>(null);

  /**
   * Where the native picture should draw, kept current through every way the
   * square moves. Written into a ref by the effect below, so that whoever asks
   * for a report gets one measured off the frame the square actually occupies.
   */
  const reportPip = useRef<() => void>(() => {});
  const frame = useRef(0);

  /**
   * One report a paint at most, and one that always runs: the frame is not
   * cancelled by the render that follows it, only by unmounting.
   *
   * The throttle is the same one setFrame in RecordControl uses and is here
   * for the same reason — a drag re-runs the effect below on every
   * pointermove, and the pointer moves faster than the screen paints. What is
   * deliberate is where the frame is *held*: requested inside the effect, it
   * is cancelled by the next move, and whether it ever runs then depends on
   * the browser's order of input dispatch and animation callbacks, which is
   * not a thing to depend on for the picture in a call.
   */
  const scheduleReport = useCallback(() => {
    if (frame.current) return;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = 0;
      reportPip.current();
    });
  }, []);

  useEffect(() => () => window.cancelAnimationFrame(frame.current), []);

  useLayoutEffect(() => {
    if (!native) return;
    const element = box.current;
    if (!element) return;
    reportPip.current = () => {
      const rect = element.getBoundingClientRect();
      setCallLayout({
        local: 'pip',
        rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
        mirror,
        swapped,
      });
    };
    scheduleReport();
  }, [native, mirror, swapped, drag, spot, scheduleReport]);

  useLayoutEffect(() => {
    if (!native) return;
    const element = box.current;
    if (!element) return;
    // Size changes arrive without a drag — a rotation, the viewport — and
    // reach the same report the drag feeds.
    const resized = new ResizeObserver(() => reportPip.current());
    resized.observe(element);
    return () => {
      resized.disconnect();
      // The pip goes with the square: minimising and the end of the video
      // phase both unmount this component, and the shell must not keep
      // drawing a picture the page no longer has a hole for.
      setCallLayout({ local: 'none', swapped: false });
    };
  }, [native]);

  /**
   * A rotation, or the shell resizing the WebView, moves the square without
   * resizing it — so the observer above never fires and the native picture
   * would be left drawing where the square used to be. The page itself needs
   * no help here: its position is a percentage, which the browser recomputes
   * on its own.
   */
  useEffect(() => {
    if (!native) return;
    const onResize = () => reportPip.current();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [native]);

  const viewport = () => ({ width: window.innerWidth, height: window.innerHeight });

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const rect = event.currentTarget.getBoundingClientRect();
    setDrag({
      // Where inside the square the finger landed, so the square follows it
      // from wherever it was taken hold of rather than jumping under its centre.
      grab: { x: event.clientX - rect.x, y: event.clientY - rect.y },
      from: { x: event.clientX, y: event.clientY },
      at: null,
    });
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const point = { x: event.clientX, y: event.clientY };
    // Nothing moves until the press has outgrown a tap: a finger never lets go
    // of exactly the pixel it pressed, and a square that shifted two pixels on
    // every swap would read as a bug rather than as a switch.
    if (!drag.at && isPipTap(drag.from, point)) return;
    const size = event.currentTarget.getBoundingClientRect();
    setDrag({ ...drag, at: pipDrag(point, drag.grab, viewport(), size) });
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>, tapped: boolean) => {
    if (!drag) return;
    const place = drag.at;
    setDrag(null);
    // A press that never moved is the swap. A cancelled one — the system
    // taking the pointer away mid-drag — keeps wherever it had got to, which
    // is the only answer that does not teleport the square out from under a
    // finger that is still on the screen.
    if (!place) {
      if (tapped) onSwap();
      return;
    }
    setSpot(pipSpot(place, viewport(), event.currentTarget.getBoundingClientRect()));
  };

  // While a finger is on it, pixels; afterwards the fraction, which the CSS
  // turns back into pixels against whatever the viewport is by then. Null is
  // the resting place the stylesheet chooses on its own.
  const style: React.CSSProperties = drag?.at
    ? { left: drag.at.x, top: drag.at.y }
    : spot
      ? ({ '--pip-x': spot.x, '--pip-y': spot.y } as React.CSSProperties)
      : {};

  return (
    <div
      ref={box}
      className={[
        'call-pip',
        drag?.at ? 'is-dragging' : '',
        spot && !drag?.at ? 'is-placed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      role="button"
      tabIndex={0}
      aria-label={swapped ? 'Показать собеседника крупно' : 'Показать себя крупно'}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onSwap();
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => onPointerUp(event, true)}
      onPointerCancel={(event) => onPointerUp(event, false)}
    >
      {off ? (
        // Opaque on purpose, in both modes: over the native picture this is
        // what stands in for the camera being off, no layout call needed.
        <div className="call-pip-off">
          <VideoIcon off size={26} />
        </div>
      ) : native ? null : ( // Native: an empty, transparent hole the shell draws through.
        <video
          ref={video}
          className="call-video-local"
          data-mirror={mirror ? '' : undefined}
          autoPlay
          playsInline
          muted
        />
      )}
    </div>
  );
}
